import * as React from "react";
import {
  Grid2 as Grid,
  Dialog,
  DialogTitle,
  DialogActions,
  DialogContent,
  Button,
  TextField,
  DialogContentText,
  Tooltip,
  IconButton,
} from "@mui/material";
import { connect } from "react-redux";
import FroalaEditorView from "react-froala-wysiwyg/FroalaEditorView";
import "froala-editor/js/plugins/colors.min.js";

import {
  Theme,
  createStyles,
} from "@mui/material/styles";
import type { WithStyles } from "@mui/styles";
import { withStyles } from "@mui/styles";
import {
  IGenericUserProps,
  genericMapStateToProps,
} from "../../DataClasses/GenericUser";
import idssStyle from "../../Configuration/SharedStyling";
import SharedMaterialReactTable, { ISharedTableColumn } from "../Shared/MaterialReactTable";
import { AjaxError } from "rxjs/ajax";
import { Delete, Edit } from "@mui/icons-material";
import froalaBaseConfig from "../../Utilities/BaseFroalaConfig";
import SharedUtility from "../../Utilities/SharedUtility";
import { Redirect } from "react-router-dom";
import { firstValueFrom } from 'rxjs';
import { ICommunicationPayload } from "../../DataClasses/CommunicationData";
import CommunicationsService from "../Services/CommunicationsService";
import { useDropzone } from 'react-dropzone';


const styles = (theme: Theme) => createStyles({ ...idssStyle(theme) });

interface IPdfDropzoneProps {
  onDrop: (base64: string) => void;
  currentPdf: string;
}

const PdfDropzone: React.FC<IPdfDropzoneProps> = ({ onDrop, currentPdf }) => {
  const [fileName, setFileName] = React.useState<string>('');
  const handleDrop = React.useCallback((acceptedFiles: File[]) => {
    if (acceptedFiles.length > 0) {
      const file = acceptedFiles[0];
      setFileName(file.name);
      const reader = new FileReader();
      
      reader.onload = () => {
        const base64String = reader.result as string;
        // Extract only the base64 part (remove the data:application/pdf;base64, prefix)
        const base64 = base64String.split(',')[1];
        onDrop(base64);
      };
      
      reader.readAsDataURL(file);
    }
  }, [onDrop]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: handleDrop,
    accept: {
      'application/pdf': ['.pdf']
    },
    multiple: false
  });

  return (
    <div
      {...getRootProps()}
      style={{
        border: '2px dashed #cccccc',
        borderRadius: '4px',
        padding: '20px',
        textAlign: 'center',
        cursor: 'pointer',
        backgroundColor: isDragActive ? '#f0f0f0' : '#fafafa'
      }}
    >
      <input {...getInputProps()} />
      {isDragActive ? (
        <p>Drop the PDF file here...</p>
      ) : (
        <div>
          <p>Drag & drop a PDF file here, or click to select</p>
          {currentPdf && fileName && (
            <p style={{ color: 'green' }}>✓ {fileName}</p>
          )}
        </div>
      )}
    </div>
  );
};

interface ICommunicationsListStateStore {
  payload: ICommunicationPayload[];
  isLoading: boolean;
  openEditDialog: boolean;
  openDeleteDialog: boolean;
  editingItemId: number;
  editingBase64Pdf: string;
  editingDisplayOrder: string;
  editingTitle: string;
  redirect: boolean;
}

type IStylesProps = WithStyles<typeof styles>;

class CommunicationsListView extends React.Component<
  IGenericUserProps & IStylesProps,
  ICommunicationsListStateStore
> {
  constructor(props: any) {
    super(props);

    this.state = {
      payload: [],
      isLoading: true,
      openEditDialog: false,
      openDeleteDialog: false,
      editingItemId: -1,
      editingBase64Pdf: "",
      editingDisplayOrder: "-1",
      editingTitle: "",
      redirect: false
    };
  }

  public componentDidMount() {
    this.RefreshModel();
  }

  private redirectToErrorPage() {
    return <Redirect to='/errorpage' />;
  }

  private setRedirectState() {
    this.setState({ redirect: true });
  }

  // Define table columns
  private getTableColumns = (): ISharedTableColumn<any>[] => [
    {
      accessorKey: 'id',
      header: 'Id',
      size: 80,
      enableSorting: true,
      enableFiltering: true,
    },
    {
      accessorKey: 'title',
      header: 'Title',
      enableSorting: false,
      enableFiltering: true,
      Cell: ({ cell }) => (
        <FroalaEditorView model={cell.getValue()} config={{ ...froalaBaseConfig }} />
      ),
    },
    {
      accessorKey: 'displayOrder',
      header: 'Display Order',
      size: 120,
      enableSorting: true,
      enableFiltering: true,
    },
    {
      accessorKey: 'createUser',
      header: 'Create User',
      size: 150,
      enableSorting: true,
      enableFiltering: true,
    },
    {
      accessorKey: 'createDate',
      header: 'Create Date',
      size: 120,
      enableSorting: true,
      enableFiltering: true,
    },
    {
      accessorKey: 'actions',
      header: 'Actions',
      size: 120,
      enableSorting: false,
      enableFiltering: false,
      enableColumnActions: false,
      Cell: ({ row }) => (
        <div style={{ display: 'flex', gap: '8px' }}>
          <Tooltip title="Edit Communication" aria-label="editCommunication">
            <IconButton
              size="small"
              aria-label="editCommunication"
              onClick={() => this.editDialogOpen(row.original.id)}
            >
              <Edit fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete Communication" aria-label="deleteCommunication">
            <IconButton
              size="small"
              aria-label="deleteCommunication"
              onClick={() => this.deleteDialogOpen(row.original.id)}
            >
              <Delete fontSize="small" />
            </IconButton>
          </Tooltip>
        </div>
      ),
    },
  ];

  public render() {
    if (this.state.redirect) {
      return this.redirectToErrorPage();
    }
    const { classes } = this.props;

    const tableData = this.state.payload.map((item: ICommunicationPayload) => ({
      id: item.id,
      title: item.title,
      base64Pdf: item.base64Pdf,
      displayOrder: item.displayOrder,
      createUser: item.lastChangedBy,
      createDate: SharedUtility.ToLocaleDateString$(item.lastChangedOn),
    }));

    return (
      <Grid container={true} className={classes.gridRowFlex} spacing={2}>
        <Grid size={12}>
          <div>
            <Button
              variant="contained"
              color="primary"
              onClick={this.addNewCommunicationBtnClicked.bind(this)}
              className={classes.mb2}
            >
              Add New Communication
            </Button>

            <SharedMaterialReactTable
              data={tableData}
              columns={this.getTableColumns()}
              title="Manage Communication"
              isLoading={this.state.isLoading}
              enablePagination={true}
              enableSorting={true}
              enableFiltering={true}
              enableColumnActions={true}
              enableRowActions={false}
              initialState={{
                pagination: { pageIndex: 0, pageSize: 15 },
              }}
              muiPaginationProps={{
                rowsPerPageOptions: [10, 15, 20, 25, 50, 75, 100, 500, 1000],
              }}
              emptyMessage="No communications found."
            />
          </div>
          <Dialog open={this.state.openEditDialog}>
            <DialogTitle id="max-width-dialog-title">
              Add/Edit Communication <i>(ID: {this.state.editingItemId})</i> 
            </DialogTitle>
            <DialogContent>
              <div style={{ overflowX: 'auto', width: '100%' }}>
                <table>
                  <tbody>
                  <tr>
                    <td>
                      <TextField
                        variant="outlined"
                        label="Display Order"
                        value={this.state.editingDisplayOrder}
                        onChange={(e) => this.handleDisplayOrderChange(e)}
                        fullWidth
                        margin="normal"
                      />
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <TextField
                        variant="outlined"
                        label="Title"
                        value={this.state.editingTitle}
                        onChange={(e) => this.setState({ editingTitle: e.target.value })}
                        fullWidth
                        margin="normal"
                       />
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <PdfDropzone
                        onDrop={(base64) => this.setState({ editingBase64Pdf: base64 })}
                        currentPdf={this.state.editingBase64Pdf}
                      />
                    </td>
                  </tr>
                </tbody>
              </table>
              </div>
            </DialogContent>
            <DialogActions>
              <Button
                onClick={this.handleEditDialogSave.bind(this)}
              >
                Save
              </Button>
              <Button
                className={classes.disabledButton}
                onClick={this.handleEditDialogClose.bind(this)}
              >
                Cancel
              </Button>
            </DialogActions>
          </Dialog>
          <Dialog
            open={this.state.openDeleteDialog}
            onClose={this.deleteDialogClose.bind(this)}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
          >
            <DialogTitle id="alert-dialog-title">
              {"Are you sure you want to delete this communication?"}
            </DialogTitle>
            <DialogContent>
              <DialogContentText id="alert-dialog-description">
                The item will be removed from communications list.
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button
                onClick={this.handleDeleteCommunication.bind(this)}
                color="primary"
                autoFocus
              >
                Yes
              </Button>
              <Button
                onClick={this.deleteDialogClose.bind(this)}
                color="primary"
              >
                No
              </Button>
            </DialogActions>
          </Dialog>
        </Grid>
      </Grid>
    );
  }

  private deleteDialogClose() {
    this.setState({ openDeleteDialog: false, editingItemId: -1 });
  }
  
  private async handleDeleteCommunication() {
    await firstValueFrom(
      CommunicationsService.DeleteCommunication$(
        this.props.user,
        this.state.editingItemId
      )
    );
    await this.RefreshModel();
    this.deleteDialogClose();
  }
  
  private async handleEditDialogSave() {
    await firstValueFrom(
      CommunicationsService.SaveCommunication$(
        this.props.user,
        this.state.editingItemId,
        this.state.editingBase64Pdf,
        this.state.editingDisplayOrder,
        this.state.editingTitle
      )
    );
    await this.RefreshModel();
    this.handleEditDialogClose();
  }
  
  private handleEditDialogClose() {
    this.setState({
      openEditDialog: false,
      editingDisplayOrder: "-1",
      editingBase64Pdf: "",
      editingTitle: "",
      editingItemId: -1,
    });
  }
  
  private handleDisplayOrderChange(e) {
    if (Number(e.target.value) && !e.target.value.includes("."))
      this.setState({ editingDisplayOrder: e.target.value });
  }

  private editDialogOpen(value: any) {
    console.log(this.state.payload);
    const item = this.state.payload.filter((x) => x.id.toString() === value.toString())[0];
    console.log("found item");
    console.log(item);

    this.setState({
      editingItemId: item.id,
      editingBase64Pdf: item.base64Pdf,
      editingDisplayOrder: item.displayOrder.toString(),
      editingTitle: item.title,
      openEditDialog: true,
    });
  }

  private deleteDialogOpen(value) {
    this.setState({ editingItemId: value, openDeleteDialog: true });
  }
  
  private addNewCommunicationBtnClicked() {
    this.setState({
      editingItemId: -1,
      editingBase64Pdf: "",
      editingDisplayOrder: "-1",
      editingTitle: "",
      openEditDialog: true,
    });
  }

  private async RefreshModel() {
    this.setState({ isLoading: true });
    const retval = await firstValueFrom(
      CommunicationsService.GetCommunicationsDetail$(this.props.user)
    );
    if (retval instanceof AjaxError || !retval) {
      this.setRedirectState();
    } else {
      this.setState({ payload: retval });
    }
    this.setState({ isLoading: false });
  }
}

// REDUX CONTAINER
const CommunicationsListPageWithState = connect<IGenericUserProps>(
  genericMapStateToProps
)(CommunicationsListView);

// STYLES CONTAINER
const CommunicationsListPage = withStyles(styles)(
  CommunicationsListPageWithState
);

export default CommunicationsListPage;
